'use client';

import { twMerge } from '@shared/themes';
import { NumberFormats, useCurrencyFormatter } from '@shared/utils';
import { useTranslations } from 'next-intl';
import type { ComponentProps } from 'react';
import type { LotteryName } from '../../../../lottery-purchases/types';
import { LotteryNumber } from '../../../../LotteryNumber/LotteryNumber';
import { LotteryNumbersDivider } from '../../../../LotteryNumbersDivider/LotteryNumbersDivider';

const YOUR_NUMBERS_LINE_PILL_CLASSES =
  'flex h-12 w-full items-center justify-center overflow-clip rounded-full border border-border-active bg-background shadow-foreground shadow-[0_3px_0_0]';

type YourNumbersMatching = {
  numbers?: number[];
  extraNumbers?: number[];
  isHighlighted?: boolean;
};

type YourNumbersLineResult = {
  numbers: number[];
  extraNumbers?: number[];
};

type YourNumbersLineProps = ComponentProps<'div'> & {
  lotteryName: LotteryName;
  result: YourNumbersLineResult;
  matching?: YourNumbersMatching;
  /** Prize for this line, in cents. */
  prize?: number;
};

function YourNumbersLinePill({ className, ...props }: ComponentProps<'div'>) {
  return <div className={twMerge(YOUR_NUMBERS_LINE_PILL_CLASSES, className)} {...props} />;
}

function getIsMatched(number: number, matchedNumbers?: number[]) {
  return matchedNumbers?.includes(number) ? true : undefined;
}

function getMatchedCount(matching?: YourNumbersMatching) {
  if (!matching) return undefined;
  return (matching.numbers?.length ?? 0) + (matching.extraNumbers?.length ?? 0);
}

function YourNumbersLine({ lotteryName, result, matching, prize, className, ...props }: YourNumbersLineProps) {
  const t = useTranslations('lotteries.results.your_numbers');
  const currencyFormatter = useCurrencyFormatter();
  const hasPrize = prize !== undefined && prize > 0;
  const formattedPrize = hasPrize ? currencyFormatter(prize).format(NumberFormats.WITH_DECIMALS).get() : undefined;
  // Matching is conveyed visually by colour and a decorative dot, so it also needs a text equivalent.
  const matchedCount = getMatchedCount(matching);

  return (
    <YourNumbersLinePill className={twMerge('px-3.5', hasPrize && 'gap-2', className)} {...props}>
      {/* A prize suffix competes with the numbers for width, so the numbers tighten up to keep all of them on screen. */}
      <div className={twMerge('flex min-w-0 flex-1 items-center justify-center', hasPrize ? 'gap-2 px-1' : 'gap-3.5 px-4')}>
        {result.numbers.map((number, index) => (
          <LotteryNumber
            key={`number-${index}`}
            className="paragraph-strong text-center whitespace-nowrap"
            variant="filled"
            isMatched={getIsMatched(number, matching?.numbers)}
            isHighlighted={matching?.isHighlighted}
          >
            {number}
          </LotteryNumber>
        ))}
        <LotteryNumbersDivider isVisible={Boolean(result.extraNumbers?.length)} lotteryName={lotteryName} />
        {result.extraNumbers?.map((number, index) => (
          <LotteryNumber
            key={`extra-${index}`}
            className="paragraph-strong text-center whitespace-nowrap"
            variant="filled"
            isMatched={getIsMatched(number, matching?.extraNumbers)}
            isHighlighted={matching?.isHighlighted}
          >
            {number}
          </LotteryNumber>
        ))}
        {matchedCount !== undefined ? (
          <span className="sr-only" data-testid="your-numbers-line-matches">
            {t('line_matches', { count: matchedCount })}
          </span>
        ) : null}
      </div>
      {hasPrize && formattedPrize ? (
        <div className="flex shrink-0 items-center justify-end rounded-2xl bg-success-background px-1.5 py-1" data-testid="your-numbers-line-prize">
          <span className="sr-only">{t('line_prize', { prize: formattedPrize })}</span>
          <span className="paragraph-explanatory-strong text-foreground text-right whitespace-nowrap" aria-hidden>
            {formattedPrize}
          </span>
        </div>
      ) : null}
    </YourNumbersLinePill>
  );
}

export { YourNumbersLine, YourNumbersLinePill, type YourNumbersLineProps, type YourNumbersMatching };
